"use client";

import {
	createContext,
	useContext,
	useEffect,
	useState,
	useCallback,
} from "react";
import type { TPropsWithChildren, TCognitoUserInfo } from "@/types";
import {
	COGNITO_ID_TOKEN,
	COGNITO_REFRESH_TOKEN,
	COGNITO_ACCESS_TOKEN,
	FULL_LOGOUT_URL,
	AUTH_REDIRECT_URI_CMS,
	AUTH_REDIRECT_URI,
} from "@/lib/constants/auth.constants";
import { useSearchParams, usePathname } from "next/navigation";
import { useRouter } from "next-nprogress-bar";
import authApi from "@/lib/api/auth.api";
import { pathNames } from "@/lib/constants/pathNames.constants";

export type TAuthContextType = {
	getAccessToken: () => string | null;
	cognitoUserInfo: TCognitoUserInfo | undefined;
	cognitoFullLogout: () => void;
	cognitoPartialLogout: () => void;
	removeCognitoStorageItems: () => void;
	saveCognitoStorageItems: (response: any) => void;
	isAuthenticated: boolean;
	isAuthenticatingInProgress: boolean;
};

const AuthContext = createContext<TAuthContextType | undefined>(undefined);

// Extract the user info from the idToken
function parseCognitoIdToken(idToken: string): TCognitoUserInfo {
	const base64Token = idToken.split(".")[1].replace("-", "+").replace("_", "/");
	const {
		given_name: givenName,
		family_name: familyName,
		email,
		sub,
		"cognito:username": username,
	} = JSON.parse(window.atob(base64Token));

	return { givenName, familyName, email, sub, username };
}

export default function AuthProvider({ children }: TPropsWithChildren) {
	const [cognitoUserInfo, setCognitoUserInfo] = useState<
		TCognitoUserInfo | undefined
	>();
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [isAuthenticatingInProgress, setIsAuthenticatingInProgress] =
		useState(true);
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();

	const getAccessToken = () => {
		return localStorage.getItem(COGNITO_ACCESS_TOKEN);
	};

	const removeCognitoStorageItems = useCallback(() => {
		localStorage.removeItem(COGNITO_ACCESS_TOKEN);
		localStorage.removeItem(COGNITO_ID_TOKEN);
		localStorage.removeItem(COGNITO_REFRESH_TOKEN);
		setIsAuthenticated(false);
	}, []);

	const saveCognitoStorageItems = useCallback((response: any) => {
		window.localStorage.setItem(COGNITO_ID_TOKEN, response.id_token);
		if (response.refresh_token)
			window.localStorage.setItem(
				COGNITO_REFRESH_TOKEN,
				response.refresh_token
			);
		window.localStorage.setItem(COGNITO_ACCESS_TOKEN, response.access_token);
	}, []);

	const cognitoFullLogout = useCallback(() => {
		removeCognitoStorageItems();
		// the page gets redirected anyway, but until that a loader can be displayed
		setIsAuthenticatingInProgress(true);
		window.location.href = FULL_LOGOUT_URL;
	}, []);

	const cognitoPartialLogout = useCallback(() => {
		removeCognitoStorageItems();
	}, []);

	useEffect(() => {
		const code = searchParams.get("code");

		if (!code) {
			const accessToken = localStorage.getItem(COGNITO_ACCESS_TOKEN);
			const idToken = localStorage.getItem(COGNITO_ID_TOKEN);

			// Verify if the access_token is still valid or not
			if (!accessToken || !idToken) {
				// no tokens in the Local Storage, go to Login
				removeCognitoStorageItems();
				setIsAuthenticated(false);
				setTimeout(() => {
					setIsAuthenticatingInProgress(false);
				}, 500);
				return;
			}

			// Validate the token from the Local storage
			authApi
				.getCognitoUserInfo(accessToken)
				.then(() => {
					// save the user info
					setCognitoUserInfo(parseCognitoIdToken(idToken));
					// auth is finished
					setIsAuthenticated(true);
				})
				.catch(async () => {
					const refreshToken = localStorage.getItem(COGNITO_REFRESH_TOKEN);
					try {
						// try to refresh the invalid token
						const response = await authApi.getCognitoToken({
							urlCode: refreshToken!,
							refreshToken: refreshToken!,
						});
						// save the user info
						const cognitoUserInfo: TCognitoUserInfo = parseCognitoIdToken(
							response.id_token
						);
						setCognitoUserInfo(cognitoUserInfo);
						// save the tokens
						saveCognitoStorageItems(response);
						// auth is finished
						setIsAuthenticated(true);

						// refresh the page to get rid of a possible 401 error displayed on the UI
						router.refresh();
					} catch {
						// could not refresh the token, go to Login
						removeCognitoStorageItems();
						setIsAuthenticated(false);
					}
				})
				.finally(() => {
					setTimeout(() => {
						setIsAuthenticatingInProgress(false);
					}, 500);
				});
		}

		// After a successful login (Cognito) the URL should contain a "code" param
		if (code) {
			const redirectUri =
				pathname === pathNames.cms ? AUTH_REDIRECT_URI_CMS : AUTH_REDIRECT_URI;

			authApi
				.getCognitoToken({ urlCode: code, refreshToken: "", redirectUri })
				.then((response) => {
					const cognitoUserInfo: TCognitoUserInfo = parseCognitoIdToken(
						response.id_token
					);
					setCognitoUserInfo(cognitoUserInfo);
					setIsAuthenticated(true);

					// save values in the local storage
					saveCognitoStorageItems(response);

					// remove "code" param from url
					router.push(pathname);
				})
				.catch(() => {
					removeCognitoStorageItems();
				})
				.finally(() => {
					setTimeout(() => {
						setIsAuthenticatingInProgress(false);
					}, 500);
				});
		}
	}, [searchParams]); // runs on every url change

	return (
		<AuthContext.Provider
			value={{
				getAccessToken,
				isAuthenticatingInProgress,
				isAuthenticated,
				cognitoUserInfo,
				cognitoFullLogout,
				cognitoPartialLogout,
				removeCognitoStorageItems,
				saveCognitoStorageItems,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

export const useAuth = () => {
	const context = useContext(AuthContext);

	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}

	return context;
};
